import React, { useRef, useEffect } from 'react';
import { MdClose } from 'react-icons/md';

const QuickReferenceModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        ref={modalRef}
        className="bg-white border border-gray-200/60 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200/60">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Markdown Quick Reference</h2>
            <p className="text-sm text-gray-500 mt-1">Essential syntax for formatting your documents</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <MdClose size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* AI Edit Status Colors */}
          <section className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              AI Edit Status Colors
            </h3>
            <p className="text-sm text-gray-600 mb-3">When AI suggests changes, you'll see a status badge indicating the edit type:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 rounded border bg-purple-100 text-purple-800 border-purple-300 text-xs font-semibold">Section Edit</span>
                <span className="text-gray-600">→ Editing a specific section</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 rounded border bg-orange-100 text-orange-800 border-orange-300 text-xs font-semibold">Full Document Edit</span>
                <span className="text-gray-600">→ Entire document changes</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 rounded border bg-blue-100 text-blue-800 border-blue-300 text-xs font-semibold">Embedded Questions</span>
                <span className="text-gray-600">→ Adding questions to document</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 rounded border bg-green-100 text-green-800 border-green-300 text-xs font-semibold">Text Selection Edit</span>
                <span className="text-gray-600">→ Editing selected text</span>
              </div>
            </div>
          </section>

          {/* Text Formatting */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Text Formatting</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">**Bold text**</code>
                <span className="text-gray-400">→</span>
                <strong className="font-bold">Bold text</strong>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">*Italic text*</code>
                <span className="text-gray-400">→</span>
                <em className="italic">Italic text</em>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">~~Strikethrough~~</code>
                <span className="text-gray-400">→</span>
                <del className="line-through">Strikethrough</del>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">`Inline code`</code>
                <span className="text-gray-400">→</span>
                <code className="bg-gray-200 px-2 py-1 rounded text-sm">Inline code</code>
              </div>
            </div>
          </section>

          {/* Headers */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Headers</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600"># Header 1</code>
                <span className="text-gray-400">→</span>
                <h1 className="text-2xl font-bold">Header 1</h1>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">## Header 2</code>
                <span className="text-gray-400">→</span>
                <h2 className="text-xl font-semibold">Header 2</h2>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">### Header 3</code>
                <span className="text-gray-400">→</span>
                <h3 className="text-lg font-medium">Header 3</h3>
              </div>
            </div>
          </section>

          {/* Lists */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Lists</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="mb-2">
                  <code className="text-sm font-mono text-gray-600">- Item 1</code><br />
                  <code className="text-sm font-mono text-gray-600">- Item 2</code>
                </div>
                <div className="text-gray-400 text-center">↓</div>
                <ul className="list-disc list-inside mt-2">
                  <li>Item 1</li>
                  <li>Item 2</li>
                </ul>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="mb-2">
                  <code className="text-sm font-mono text-gray-600">1. Item 1</code><br />
                  <code className="text-sm font-mono text-gray-600">2. Item 2</code>
                </div>
                <div className="text-gray-400 text-center">↓</div>
                <ol className="list-decimal list-inside mt-2">
                  <li>Item 1</li>
                  <li>Item 2</li>
                </ol>
              </div>
            </div>
          </section>

          {/* Math & LaTeX */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Math & LaTeX</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">$E = mc^2$</code>
                <div className="text-gray-400 text-center mt-2">↓</div>
                <div className="mt-2 text-center">Inline math: E = mc²</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">{'$$x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$$'}</code>
                <div className="text-gray-400 text-center mt-2">↓</div>
                <div className="mt-2 text-center">Display math block</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">{'\\(x^2 + y^2 = r^2\\)'}</code>
                <div className="text-gray-400 text-center mt-2">↓</div>
                <div className="mt-2 text-center">LaTeX inline: x² + y² = r²</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">{'\\[E = h\\nu\\]'}</code>
                <div className="text-gray-400 text-center mt-2">↓</div>
                <div className="mt-2 text-center">LaTeX display block</div>
              </div>
            </div>
          </section>

          {/* Links & Images */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Links & Images</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">[Link text](https://example.com)</code>
                <span className="text-gray-400">→</span>
                <span className="text-blue-600 underline cursor-pointer">Link text</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">![Alt text](image.jpg)</code>
                <span className="text-gray-400">→</span>
                <span className="text-gray-600">Embedded image</span>
              </div>
            </div>
          </section>

          {/* MATLAB/Octave Code Blocks */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">MATLAB/Octave Code</h3>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="mb-2">
                  <code className="text-sm font-mono text-gray-600">```matlab:runnable</code><br />
                  <code className="text-sm font-mono text-gray-600">x = 1:10;</code><br />
                  <code className="text-sm font-mono text-gray-600">plot(x, x.^2);</code><br />
                  <code className="text-sm font-mono text-gray-600">```</code>
                </div>
                <div className="text-gray-400 text-center">↓</div>
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                  <strong className="text-green-800">Runnable code block</strong> - Users can execute this code
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="mb-2">
                  <code className="text-sm font-mono text-gray-600">```matlab</code><br />
                  <code className="text-sm font-mono text-gray-600">function y = square(x)</code><br />
                  <code className="text-sm font-mono text-gray-600">{'    '}y = x.^2;</code><br />
                  <code className="text-sm font-mono text-gray-600">end</code><br />
                  <code className="text-sm font-mono text-gray-600">```</code>
                </div>
                <div className="text-gray-400 text-center">↓</div>
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                  <strong className="text-blue-800">Code example</strong> - Syntax highlighting only
                </div>
              </div>

              {/* Multi-language tabs */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="mb-2">
                  <code className="text-sm font-mono text-gray-600">```python:runnable</code><br />
                  <code className="text-sm font-mono text-gray-600">print("hello")</code><br />
                  <code className="text-sm font-mono text-gray-600">```</code><br />
                  <br />
                  <code className="text-sm font-mono text-gray-600">```matlab:runnable</code><br />
                  <code className="text-sm font-mono text-gray-600">disp('hello')</code><br />
                  <code className="text-sm font-mono text-gray-600">```</code>
                </div>
                <div className="text-gray-400 text-center">↓</div>
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                  <strong className="text-amber-800">Tabbed multi-language snippet</strong> — two or more
                  <code className="mx-1 px-1 bg-amber-100 rounded">language:runnable</code>
                  blocks placed back-to-back render as one runnable snippet with a tab per language. Each tab keeps its own code, output, and run state. Any text, heading, or list between the fences will split them back into separate snippets.
                </div>
              </div>
            </div>
          </section>

          {/* Embedded Questions */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Embedded Questions</h3>
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="mb-2 text-sm">
                  <strong className="text-purple-700">Multiple Choice Question:</strong>
                </div>
                <code className="text-xs font-mono text-gray-600 block">```question:mcq</code>
                <code className="text-xs font-mono text-gray-600 block">ID: Q1</code>
                <code className="text-xs font-mono text-gray-600 block">TYPE: single</code>
                <code className="text-xs font-mono text-gray-600 block">LO: Understanding of concept</code>
                <code className="text-xs font-mono text-gray-600 block">QUESTION:</code>
                <code className="text-xs font-mono text-gray-600 block">What does this function do?</code>
                <code className="text-xs font-mono text-gray-600 block">OPTIONS:</code>
                <code className="text-xs font-mono text-gray-600 block">A: Option 1</code>
                <code className="text-xs font-mono text-gray-600 block">B: Option 2</code>
                <code className="text-xs font-mono text-gray-600 block">C: Option 3</code>
                <code className="text-xs font-mono text-gray-600 block">RUBRIC: B</code>
                <code className="text-xs font-mono text-gray-600 block">EXPLANATION:</code>
                <code className="text-xs font-mono text-gray-600 block">Explanation text here</code>
                <code className="text-xs font-mono text-gray-600 block">```</code>
                <div className="text-gray-400 text-center mt-2">↓</div>
                <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-sm">
                  <strong className="text-purple-800">Interactive MCQ</strong> - Students can select and check answers
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="mb-2 text-sm">
                  <strong className="text-indigo-700">Short Answer Question:</strong>
                </div>
                <code className="text-xs font-mono text-gray-600 block">```question:short</code>
                <code className="text-xs font-mono text-gray-600 block">ID: Q2</code>
                <code className="text-xs font-mono text-gray-600 block">LO: Application of concept</code>
                <code className="text-xs font-mono text-gray-600 block">QUESTION:</code>
                <code className="text-xs font-mono text-gray-600 block">Explain the concept in your own words.</code>
                <code className="text-xs font-mono text-gray-600 block">RUBRIC:</code>
                <code className="text-xs font-mono text-gray-600 block">Expected answer or key points</code>
                <code className="text-xs font-mono text-gray-600 block">```</code>
                <div className="text-gray-400 text-center mt-2">↓</div>
                <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-sm">
                  <strong className="text-indigo-800">Short Answer</strong> - Text input with rubric for instructors
                </div>
              </div>
            </div>
          </section>

          {/* Other Elements */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Other Elements</h3>
            <div className="space-y-2">
              <div className="p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">&gt; Blockquote text</code>
                <div className="text-gray-400 text-center mt-2">↓</div>
                <blockquote className="border-l-4 border-blue-300 pl-4 italic text-gray-700 mt-2">
                  Blockquote text
                </blockquote>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <code className="text-sm font-mono text-gray-600">---</code>
                <div className="text-gray-400 text-center mt-2">↓</div>
                <hr className="mt-2 border-t-2 border-gray-200" />
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50/50 border-t border-gray-200/60 rounded-b-2xl">
          <div className="space-y-2">
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
              <span>💡</span>
              <span className="font-medium">Pro Tips:</span>
            </div>
            <ul className="text-sm text-gray-500 space-y-1 max-w-2xl mx-auto">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Select text in the editor to edit it with AI assistance</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Use the chat bar to ask AI to "improve the intro" or "add questions to the document"</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Review all AI suggestions in the diff preview before accepting changes</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Scroll is locked when reviewing edits - accept or decline to continue editing</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickReferenceModal;